import React, { useState, useRef } from "react";
import Toast from "../toast";
import PropTypes from "prop-types";
import DestinationsModal from "../builder-v2/modals/DestinationsModal";
import { bulkSyncCohorts, isConcurrentChange, versionsOf } from "../../services/cohort";
import {
	DV360_ACTIVATION_METHOD_ID,
	formatDv360SlotsSuffix,
	formatDv360SlotErrorsSuffix,
} from "../../utilities/dv360Slots";

function UpdateActivationMethods({
	cohort,
	tagCohorts,
	getCohorts,
	onUpdate,
	pullCohorts
}) {
	const uniqueIds = cohort ? cohort.map((cohort) => cohort.value) : [];
	const [toast, setToast] = useState(null);
	const [loading, setLoading] = useState(false);
	const timerRef = useRef(null);

	const action = pullCohorts ? "pull" : "push";
	const audienceWord = uniqueIds.length !== 1 ? "Audience(s)" : "Audience";
	const title = pullCohorts ? "Select Destinations to Pull From" : "Select Destinations to Push To";
	const saveButtonText = pullCohorts ? "Pull from Destinations" : "Push to Destinations";

	const handleConfirm = (selected) => {
		setLoading(true);

		const dv360Selected = selected.some((d) => d.id === DV360_ACTIVATION_METHOD_ID);

		bulkSyncCohorts(uniqueIds, action, selected, localStorage.getItem("activeSilo"), versionsOf(cohort))
			.then((res) => {
				setLoading(false);
				// Reload now, not when the toast closes: the rows' versions moved (ASB-4801).
				getCohorts(localStorage.getItem("activeSilo"));
				const baseMessage = pullCohorts ? `${audienceWord} Pulled` : `${audienceWord} Pushed`;
				if (dv360Selected && res?.dv360SlotErrors && Object.keys(res.dv360SlotErrors).length) {
					setToast({
						message: baseMessage + formatDv360SlotErrorsSuffix(res.dv360SlotErrors, cohort),
						status: "Info",
						title: "DV360 slot not assigned",
					});
				} else {
					setToast({
						message: baseMessage + (dv360Selected ? formatDv360SlotsSuffix(res?.dv360Slots, cohort) : ""),
						status: "Success",
						title: "Success",
					});
				}
			})
			.catch((e) => {
				setLoading(false);
				// Show the API's reason when it refuses the push (a 4xx, e.g. Index
				// Exchange Curation chosen before it is set up), as the builder does.
				// A server error's text isn't meant for users: the generic line.
				// Only a text reason: the toast renders it as-is.
				const refused = e?.status >= 400 && e?.status < 500;
				const reason = [e?.data?.message, e?.data?.detail].find((r) => typeof r === "string" && r);
				setToast({
					message: (refused && reason) || (pullCohorts ? `Error pulling ${audienceWord.toLowerCase()}` : `Error pushing ${audienceWord.toLowerCase()}`),
					status: "Invalid",
					// ASB-4801: the API names the audience someone else saved since the list
					// loaded; closing this reloads the list (postUpdate), so a retry is current.
					title: isConcurrentChange(e) ? "Not saved" : "Error",
				});
			});
	};

	const postUpdate = () => {
		getCohorts(localStorage.getItem("activeSilo"));
		onUpdate();
		// After a failure the selection stays, so the user can retry, as for archive (ASB-4801).
		if (toast?.status !== "Invalid") tagCohorts("");
	};

	const toastClose = () => {
		setToast(null);
		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}
		postUpdate();
	};

	if (toast) {
		return (
			<Toast
				status={toast.status}
				message={toast.message}
				title={toast.title}
				callback={() => toastClose()}
				autoHide={false}
			/>
		);
	}

	return (
		<DestinationsModal
			isOpen={true}
			onClose={onUpdate}
			onConfirm={handleConfirm}
			loading={loading}
			title={title}
			saveButtonText={saveButtonText}
			// A bulk push applies one destination set to many audiences at once, each with
			// its own existing state — there's no single meaningful "first push" signal to
			// prompt a price for, so this flow skips it entirely (same reason GAM's per-
			// network config section isn't wired up here either, via the omitted
			// gamNetworkCodes prop). Price stays whatever each audience already has
			// (default 1 if never customized) — set/edited via the Audience Builder's own
			// Destinations save or the Audience Manager's Equativ Price column instead.
			allowEquativPricePrompt={false}
		/>
	);
}

UpdateActivationMethods.propTypes = {
	cohort: PropTypes.any,
	tagCohorts: PropTypes.func,
	getCohorts: PropTypes.func,
	onUpdate: PropTypes.func.isRequired,
	pullCohorts: PropTypes.bool.isRequired,
};

export { UpdateActivationMethods };
